/**
 * Money in transit (D01, §15.4): integer minor units as a decimal string plus an ISO-4217 code.
 * In process it becomes `bigint` (see @windrop/core money helpers). Never a float, never a JSON number.
 */
import { z } from 'zod';
/** Minor-unit exponents for the currencies the platform ships with. XTS is the ISO testing code used by the demo tenant (D-002). */
export const CURRENCY_EXPONENTS = {
    XTS: 2,
    NGN: 2,
    USD: 2,
    GBP: 2,
    EUR: 2,
    KES: 2,
    GHS: 2,
    ZAR: 2,
    JPY: 0,
    XOF: 0,
};
export const SUPPORTED_CURRENCIES = Object.keys(CURRENCY_EXPONENTS);
export const isSupportedCurrency = (code) => Object.prototype.hasOwnProperty.call(CURRENCY_EXPONENTS, code);
/** Exponent of a currency; `undefined` for codes outside the table (the caller decides whether to reject). */
export const currencyExponent = (code) => isSupportedCurrency(code) ? CURRENCY_EXPONENTS[code] : undefined;
/** Any syntactically valid ISO-4217 alphabetic code. */
export const CurrencyCode = z.string().regex(/^[A-Z]{3}$/, 'expected an ISO-4217 code');
/** A currency the platform has an exponent table entry for (lottery configuration requires this). */
export const SupportedCurrencyCode = z.enum(SUPPORTED_CURRENCIES);
/** Non-negative integer minor units as a canonical decimal string (no sign, no leading zeros). */
export const MinorAmount = z
    .string()
    .regex(/^(?:0|[1-9]\d*)$/, 'expected integer minor units as a decimal string');
/** Signed integer minor units (ledger lines, deltas). */
export const SignedMinorAmount = z
    .string()
    .regex(/^-?(?:0|[1-9]\d*)$/, 'expected signed integer minor units');
export const MoneyDto = z.object({
    amountMinor: MinorAmount,
    currency: CurrencyCode,
});
export const SignedMoneyDto = z.object({
    amountMinor: SignedMinorAmount,
    currency: CurrencyCode,
});
/** Money whose currency must be one the platform can format and round (configuration surfaces). */
export const SupportedMoneyDto = z.object({
    amountMinor: MinorAmount,
    currency: SupportedCurrencyCode,
});
//# sourceMappingURL=money.js.map